import { parseActorAppearance, type ActorAppearancePreferences } from "@neucleos/theme";
import { useEffect, useRef, useState } from "react";
import { create } from "zustand";
import {
  adoptActorAppearance,
  getClientSettings,
  useClientSettings,
  useClientSettingsHydrated,
} from "~/hooks/useSettings";
import { useNeucleosWorldStore } from "./worldStore";
import { readAppearance, saveAppearance } from "./appearancePrefsClient";

type Standing = { status: "local" | "pending" | "synced" | "failed"; reason: string | null };
export const useAppearanceSyncStanding = create<Standing>(() => ({
  status: "local",
  reason: null,
}));
const setStanding = (status: Standing["status"], reason: string | null = null) =>
  useAppearanceSyncStanding.setState({ status, reason });
const encode = (p: unknown) => JSON.stringify(parseActorAppearance(p));
function mirror(actorId: string, preferences: ActorAppearancePreferences, dirty: boolean) {
  try {
    localStorage.setItem(`cleo:appearance:${actorId}`, JSON.stringify({ preferences, dirty }));
  } catch {
    /* Device storage may be unavailable; remote standing stays independent. */
  }
}
function cached(
  actorId: string,
): { preferences: ActorAppearancePreferences; dirty: boolean } | null {
  try {
    const raw = JSON.parse(localStorage.getItem(`cleo:appearance:${actorId}`) ?? "null");
    return raw
      ? { preferences: parseActorAppearance(raw.preferences), dirty: raw.dirty === true }
      : null;
  } catch {
    return null;
  }
}

/** One coordinator for both settings hosts. Every save is serialized and confirmed by the server's separate read. */
export function AppearancePrefsSync() {
  const settings = useClientSettings();
  const hydrated = useClientSettingsHydrated();
  const identity = useNeucleosWorldStore((s) => s.identity);
  const signedIn = useNeucleosWorldStore((s) => s.signedIn);
  const worldKey = useNeucleosWorldStore((s) => s.activeWorldKey ?? s.worlds[0]?.worldKey ?? null);
  const revision = useNeucleosWorldStore((s) => s.grantRevision);
  const [retry, setRetry] = useState(0);
  const serial = encode(settings);
  const lastSerial = useRef(serial);
  const baselineHydrated = useRef(false);
  const pendingLocal = useRef<{
    identity: string | null;
    preferences: ActorAppearancePreferences;
  } | null>(null);
  const active = useRef<{
    actorId: string;
    worldKey: string;
    updatedAt: number | null;
    saved: string;
    generation: number;
  } | null>(null);
  const generation = useRef(0);
  const knownActor = useRef<string | null>(null);
  const inFlight = useRef(false);
  const queue = useRef<ActorAppearancePreferences | null>(null);
  const flushRef = useRef<() => void>(() => {});
  useEffect(() => {
    const reconnect = () => setRetry((n) => n + 1);
    window.addEventListener("online", reconnect);
    window.addEventListener("cleo-appearance-retry", reconnect);
    return () => {
      window.removeEventListener("online", reconnect);
      window.removeEventListener("cleo-appearance-retry", reconnect);
    };
  }, []);
  useEffect(() => {
    if (!hydrated) return;
    const gen = ++generation.current;
    active.current = null;
    queue.current = null;
    inFlight.current = false;
    knownActor.current = null;
    if (!signedIn || !identity || !worldKey) {
      setStanding("local");
      return;
    }
    try {
      knownActor.current = localStorage.getItem(`cleo:appearance-owner:${identity}`);
    } catch {
      /* optional local index */
    }
    const remembered = knownActor.current ? cached(knownActor.current) : null;
    if (remembered) {
      lastSerial.current = encode(remembered.preferences);
      adoptActorAppearance(remembered.preferences);
    }
    const initial = encode(getClientSettings());
    setStanding("pending");
    void readAppearance(worldKey).then((outcome) => {
      if (generation.current !== gen) return;
      if (!outcome.ok) {
        setStanding("failed", outcome.reason);
        return;
      }
      knownActor.current = outcome.actorId;
      try {
        localStorage.setItem(`cleo:appearance-owner:${identity}`, outcome.actorId);
      } catch {
        /* optional local index */
      }
      const local = cached(outcome.actorId);
      const changedDuringRead = encode(getClientSettings()) !== initial;
      const next =
        pendingLocal.current?.identity === identity
          ? pendingLocal.current.preferences
          : changedDuringRead
            ? parseActorAppearance(getClientSettings())
            : local?.dirty
              ? local.preferences
              : (outcome.preferences ?? parseActorAppearance(getClientSettings()));
      const saved = outcome.preferences ? encode(outcome.preferences) : "";
      active.current = {
        actorId: outcome.actorId,
        worldKey,
        updatedAt: outcome.updatedAt,
        saved,
        generation: gen,
      };
      mirror(outcome.actorId, next, encode(next) !== saved);
      lastSerial.current = encode(next);
      adoptActorAppearance(next);
      if (encode(next) !== saved) {
        queue.current = next;
        flushRef.current();
      } else setStanding("synced");
    });
    return () => {
      generation.current++;
    };
  }, [hydrated, identity, signedIn, worldKey, revision, retry]);

  flushRef.current = () => {
    const seat = active.current;
    if (!seat || inFlight.current || !queue.current) return;
    const next = queue.current;
    queue.current = null;
    inFlight.current = true;
    mirror(seat.actorId, next, true);
    setStanding("pending");
    void saveAppearance(seat.worldKey, next, seat.updatedAt).then((outcome) => {
      if (generation.current !== seat.generation) return;
      inFlight.current = false;
      if (!outcome.ok || outcome.actorId !== seat.actorId) {
        setStanding("failed", outcome.ok ? "The signed-in account changed." : outcome.reason);
        return;
      }
      seat.saved = encode(next);
      seat.updatedAt = outcome.updatedAt;
      if (pendingLocal.current && encode(pendingLocal.current.preferences) === seat.saved)
        pendingLocal.current = null;
      if (queue.current && encode(queue.current) !== seat.saved) {
        flushRef.current();
        return;
      }
      queue.current = null;
      mirror(seat.actorId, next, false);
      setStanding("synced");
    });
  };
  useEffect(() => {
    if (!hydrated) return;
    if (!baselineHydrated.current) {
      baselineHydrated.current = true;
      lastSerial.current = encode(getClientSettings());
      return;
    }
    if (serial !== encode(getClientSettings())) return;
    if (serial !== lastSerial.current) {
      lastSerial.current = serial;
      pendingLocal.current = { identity, preferences: parseActorAppearance(settings) };
    }
    const seat = active.current;
    if (!seat) {
      if (knownActor.current && pendingLocal.current?.identity === identity)
        mirror(knownActor.current, pendingLocal.current.preferences, true);
      return;
    }
    if (serial === seat.saved && !inFlight.current && !queue.current) return;
    queue.current = parseActorAppearance(settings);
    mirror(seat.actorId, queue.current, true);
    // A slider drag is one settling choice, not a request on every pixel.
    const timer = setTimeout(() => flushRef.current(), 250);
    return () => clearTimeout(timer);
  }, [serial, settings, identity, hydrated]);
  return null;
}
